/**
 * F17 T17.1.6 v3 Commit 6 — zakładka "Moje czekające" (step-level).
 * F17 T17.8 — masowe akceptacje: multi-select + pasek akcji + potwierdzenie „drogą domyślną".
 * Kokpit decyzji — przyciski dróg per wiersz (odrzucenie to droga narysowana w procesie,
 * nie osobny przycisk), kolumny „Kontrahent"/„Firma"/„Netto"/„Brutto"/„Wartość" + suma
 * per-waluta, widok kart, sort surowych kolumn, auto-odświeżanie.
 *
 * Endpoint: `GET /api/processes/my-pending-decisions` z enriched response
 * (processName + currentNodeLabel + targetLabel + submittedByUser + availableRoutes
 * + amount + counterpartyName + netAmount + grossAmount + companyName) oraz `sumsByCurrency`
 * (suma po wszystkich stronach, dedup dokumentu).
 *
 * Klik wiersza → `useNavigateToTarget` → karta rekordu: dla dokumentu klasa (a więc trasa)
 * jest dociągana async po id przez `/documents?ids=`, dla pozostałych celów synchroniczny
 * `resolveTargetUrl`. Przycisk „Otwórz formularz" w kolumnie akcji (tabela i karty) robi
 * dokładnie to samo co klik wiersza (nie ma go w wierszu nienawigowalnym). Klik checkboxa /
 * przycisków akcji NIE nawiguje — stopPropagation na kontenerze akcji + na td checkboxa
 * w `ConfigurableTable`.
 *
 * Decyzja per wiersz: `DecisionButtons` („Akceptacja" + drogi etapu) otwiera to samo okno
 * „Akceptacja" co na karcie rekordu (`AcceptanceDialog`, w opakowaniu `PendingDecisionDialog`,
 * które dociąga migawkę sprawy). Okno wymusza komentarz, gdy droga tego wymaga, i wskazanie
 * adresata, gdy cel drogi go wymaga.
 *
 * Akcja masowa „Akceptuj zaznaczone": pytanie o potwierdzenie (liczba decyzji) → wysyłka drogą
 * DOMYŚLNĄ, bez wyboru drogi i bez komentarza, paczkami po max 100 pozycji (limit serwera) →
 * jeden wspólny wynik. Pełny sukces = dymek; gdy coś pominięto (sprawa wymaga okna decyzji:
 * komentarz, adresat, brakujące pola, procedura kontrolna, droga bez domyślnej…) — okno wyniku
 * z listą pominiętych i powodem każdej. Zaakceptowane znikają z listy (także te, w których głos
 * oddano, a etap czeka na pozostałych akceptantów — lista nie pokazuje kroków, w których osoba
 * już zagłosowała), pominięte ZOSTAJĄ zaznaczone, z wyjątkiem spraw anulowanych w międzyczasie
 * (te znikają z listy razem z zaznaczeniem). Ostrzeżenie procedury kontrolnej nie jest
 * potwierdzane hurtem.
 *
 * Zaznaczenie liczy się TYLKO z wierszy wczytanych na ekranie (lista płaska, rozwinięte koszyki
 * grup albo rozwinięte sekcje kartotek): stan trzyma id, a `selectedIds` użyte niżej to jego część
 * wspólna z wczytanymi wierszami — licznik na pasku akcji, „zaznacz wszystko", sama akcja i
 * wstrzymanie auto-odświeżania widzą ten sam zbiór (zaznaczenie, które zniknęło z listy, nie
 * wstrzymuje odświeżania w nieskończoność). Zmiana strony, filtra, sortowania, grupowania, zakresu
 * lub kafelka („Przeterminowane", „Zespół") czyści zaznaczenie; rozstrzygnięcie zaznaczonej sprawy
 * przyciskami w jej wierszu zdejmuje ją z zaznaczenia.
 *
 * Filtrowalne: surowe kolumny SQL (`instance_number`/`target_module_code`/
 * `instance_submitted_at`/`priority`) ORAZ `process_name`/`submitted_by` (dociągnięte LEFT
 * JOIN-em w `route.ts`) ORAZ (fala 2 kokpitu decyzji) `target_label`/`current_node`/`amount` —
 * te trzy są wyrażeniami SQL wyliczanymi na snapshocie, ale filtrowalne/sortowalne WPROST
 * przez `HEADER_FILTER_EXPR_FIELDS`/`SORT_FIELDS` w `route.ts` (nie liczone „w JS po
 * zapytaniu" mimo braku prawdziwej kolumny Drizzle) ORAZ (fala 1 listy decyzji) `counterparty`
 * (tekst, warunek), `net_amount`/`gross_amount` (liczba) i `company_id` (firma rekordu — lista
 * rozwijana z id firm, patrz `companyOptionsQuery`). Sortowalne server-side przez `?sort=`
 * (kontrolowany `onSort` — tabela nie sortuje lokalnie pobranej strony): jak wyżej +
 * `deadline_at` + `counterparty`/`company`/`net_amount`/`gross_amount`. `current_node` jest
 * sortowalna WYŁĄCZNIE tutaj (krok pojedynczy, skalar) — ekrany spraw
 * (`InstanceListingTable.tsx`) mają ten sam filtr, ale bez sortu (krok bywa wielowartościowy).
 *
 * Zakres „Zespół" (kafelek na `ApprovalsPage`, deep-link `?scope=team`, mirror
 * `?overdue=true`) — WARTOŚĆ POCHODNA z URL, nie osobny stan (ten sam wzorzec co
 * `overdueOnly` niżej). Gdy aktywny: `fetchMyPendingDecisions({ scope: 'team' })`
 * zwraca oczekujące decyzje bezpośrednich podwładnych przełożonego zamiast usera —
 * widok jest TYLKO-PODGLĄD (`readOnly`: multi-select + pasek masowych akcji + przyciski
 * decyzji wyłączone; w kolumnie „Akcje" zostaje tylko „Otwórz formularz"). Backend zwraca
 * 403 `FORBIDDEN`, gdy user nie ma uprawnienia „podgląd zespołu" — łapiemy to łagodnie
 * (mirror `ActiveInstancesTab`), choć w praktyce kafelek wywołujący ten widok i tak jest
 * wtedy ukryty.
 *
 * Zmiana filtra, sortowania lub strony NIE chowa tabeli pod napisem „Ładowanie": do czasu
 * odpowiedzi zostają poprzednie wiersze (`placeholderData`), ale tylko w tym samym widoku —
 * „pokoleniu widoku" (zakres „Moje czekające"/„Przeterminowane"/„Zespół", wymiar grupowania lub
 * lista płaska; zmiana któregokolwiek, także powrót z grupowania do listy albo do wcześniej
 * używanego wymiaru, zaczyna nowe pokolenie) — po zmianie widoku pełne „Ładowanie", żeby nie
 * pokazać wierszy cudzego zakresu. Przy danych-zastępniku kontener tabeli jest `aria-busy` i
 * przyciemniony, a zaznaczanie i akcja masowa są wyłączone (przyciski decyzji w wierszu zostają —
 * świeży stan sprawy dociąga okno decyzji, a serwer odrzuca decyzję na nieaktualnym kroku).
 *
 * Grupowanie (`?groupBy=`, biała lista `PENDING_DECISION_GROUP_BY` z `@saas/shared`) —
 * `groupedQuery` dociąga WYŁĄCZNIE nagłówki koszyków (liczność + sumy per waluta), BEZ
 * wierszy. Koszyki startują ZWINIĘTE; klik nagłówka dociąga wiersze TEGO koszyka leniwie
 * — osobne żądanie `fetchPendingDecisionGroupRows({ groupBy, groupKey })` per rozwinięty
 * klucz, z WŁASNĄ mini-paginacją. Mechanika (stan rozwinięcia/paginacji, `useQueries`,
 * mapowanie na kształt `ConfigurableTable.groups`) żyje we WSPÓLNYM haku
 * `useLazyGroupRows` (`./use-lazy-group-rows.tsx`) — mirror `ActiveInstancesTab.tsx`/
 * `MySubmittedInstancesTab.tsx`. Ponowny klik zwija — już pobrane wiersze zostają w
 * cache react-query (kolejne rozwinięcie bez re-fetchu, dopóki nie wygasną/inwaliduje
 * ich mutacja).
 *
 * Sekcje kartotek (`?groupBy=target_class`, wszystkie trzy zakresy: „Moje czekające",
 * „Przeterminowane" i „Zespół") — kartoteka z zestawem sekcji (`decision-sections/section-kits.tsx`;
 * dokumenty Symfonii Handel mają stałe zestawy z `handel-section-kits.tsx` — osobną sekcję na
 * sprzedaż, magazyn i zamówienia; kartoteki własne `custom.<slug>` — zestaw z
 * `custom-section-kit.tsx`, z katalogiem kolumn z odpowiedzi sekcji) dostaje zamiast zwijanej
 * grupy OSOBNĄ SEKCJĘ z kolumnami listy tej kartoteki i kolumną „Decyzja" (`DecisionSections`);
 * sekcje z kolumnami kwot mają pod tabelą wiersz „Razem", a w nagłówku „Wartość" sekcji
 * (kartoteki własne — tylko stopka). Nad treścią stoi wtedy osobny pasek z selektorem
 * grupowania, przełącznikiem auto-odświeżania (te same kontrolki i testid co w pasku tabeli),
 * przełącznikiem „Lista / Karty" i — przy zaznaczeniu — JEDNYM paskiem akcji masowej; pod
 * sekcjami — dotychczasowa tabela z POZOSTAŁYMI kartotekami (tylko gdy jakieś zostały — dziś
 * wyłącznie zabezpieczenie dla nieznanego klucza, żaden znany rodzaj kartoteki jej nie zasila).
 * Okno decyzji i pauza auto-odświeżania są wspólne z tabelą (sekcja woła `onOpenDecision`). W tym
 * trybie tabela pozostałych kartotek jest BEZ filtrów nagłówka
 * i bez sortowania (filtry i sortowanie mają same sekcje), a KAŻDE wejście w tryb czyści filtry i
 * sortowanie wspólnej tabeli — zapytanie o nagłówki koszyków nie niesie wtedy `headerFilters`, więc
 * liczniki sekcji nie zależą od żadnego filtra. Zmiana zakresu lub trybu sekcji czyści też
 * rozwinięcie grup tabeli (grupa rozwinięta w „Zespole" nie jest dalej pobierana w ukryciu), a
 * sekcje po zmianie zakresu startują od nowa (zwinięte). Decyzja z okna i akcja masowa unieważniają
 * podpowiedzi filtra sekcji (liczone z jej rekordów).
 *
 * Zaznaczanie w trybie sekcji: stan zostaje tutaj (`markedIds` = id KROKÓW), a sekcje zgłaszają
 * zakładce wczytane kroki do rejestru (`sectionSteps`, klucz = klucz sekcji; sekcja wpisuje się po
 * wczytaniu danych, a wypisuje przy zwinięciu, błędzie i danych zastępczych). `loadedRows` to wiersze
 * tabeli pozostałych kartotek + kroki sekcji, więc licznik, pauza auto-odświeżania, potwierdzenie,
 * wynik częściowy i akcja masowa liczą jedno wspólne zaznaczenie (DECYZJE, nie rekordy). Każda tabela
 * (sekcja i tabela pozostałych) widzi tylko swój wycinek zaznaczenia, a jej zmiana jest SCALANA z
 * resztą (`replaceSelectionSlice`) — „zaznacz wszystko" jednej tabeli nie kasuje zaznaczenia innych.
 * Zmiana strony / filtra / sortu w sekcji, jej zwinięcie oraz zmiana wczytanych danych, po której
 * rekord ma zaznaczoną tylko część kroków, zdejmują zaznaczenie tylko tej sekcji (szczegóły →
 * `DecisionSectionTable`). Licznik zaznaczenia ma też stały, ukryty region `role="status"` w pasku
 * trybu sekcji (`DecisionSectionsSelectionStatus`) — czytnik ekranu zapowiada zmianę liczby.
 *
 * Zakres „Zespół" w trybie sekcji jest TYLKO DO ODCZYTU (jak zwykły Zespół): sekcje bez przycisków
 * decyzji i bez zaznaczania, kolumna „Akcje" z samym „Otwórz formularz".
 *
 * Widok „Lista / Karty": wybór trzyma ta zakładka (`viewMode`) i jest WSPÓLNY dla zwykłej listy i
 * sekcji — przejście „Grupuj: Kartoteka" ↔ „Bez grupowania" go zachowuje. Obie tabele dostają go w
 * trybie sterowanym, więc wbudowanego przełącznika tabeli nie ma; zastępuje go `DecisionViewModeToggle`
 * (ten sam wygląd i `data-testid`), w pasku tabeli zwykłej listy albo w pasku trybu sekcji. Tabela
 * pozostałych kartotek w trybie sekcji zostaje tabelą (grupowanie nie ma kart). Wybór nie jest
 * zapisywany w układzie (`persistConfig={false}`) — żyje do zamknięcia zakładki.
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PENDING_DECISION_GROUP_BY, type ViewMode } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type { TFunction } from 'i18next';
import { RefreshCw } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnHeaderFilters,
  ConfigurableTable,
  invalidateSectionDistinctValues,
} from '@/components/configurable-table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import {
  BulkAcceptResultDialog,
  type BulkSkippedItem,
} from '@/components/processes/BulkAcceptResultDialog';
import { DecisionButtons, type DecisionTarget } from '@/components/processes/DecisionButtons';
import { PendingDecisionDialog } from '@/components/processes/PendingDecisionDialog';
import { PriorityBadge } from '@/components/processes/PriorityBadge';
import { useToast } from '@/contexts/ToastContext';
import {
  type BulkDecisionItem,
  type BulkDecisionResponse,
  type BulkDecisionResultItem,
  bulkSkipReason,
  bulkSubmitDecisionsInBatches,
  DECISION_GROUP_NONE_KEY,
  displaySubmitter,
  fetchGroupedPendingDecisions,
  fetchMyPendingDecisions,
  fetchPendingDecisionGroupRows,
  type MyPendingDecisionRow,
  type PendingDecisionGroupHeader,
  type PendingDecisionScope,
  TARGET_MODULE_INACTIVE_CODE,
  TARGET_RECORD_DELETED_CODE,
  type TargetAmount,
} from '@/lib/processes/approvals-api';
import { formatMoney } from '@/lib/processes/format-money';
import { isTargetNavigable, moduleLabelI18nKey } from '@/lib/processes/module-routes';
import { parsePendingGroupBy, type PendingDecisionGroupBy } from '@/lib/processes/pending-group-by';
import { useNavigateToTarget } from '@/lib/processes/use-navigate-to-target';
import { isForbiddenError, retryUnlessForbidden } from '@/lib/query';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';
import { cn } from '@/lib/utils';

import {
  DecisionBulkActions,
  DecisionSectionsBulkBar,
  DecisionSectionsSelectionStatus,
} from './decision-sections/DecisionBulkBar';
import {
  DecisionSections,
  type SectionEntry,
  splitSectionGroups,
} from './decision-sections/DecisionSections';
import { DecisionViewModeToggle } from './decision-sections/DecisionViewModeToggle';
import {
  type DecisionSectionSelection,
  replaceSelectionSlice,
} from './decision-sections/selection';
import { serializeHeaderFiltersForRequest } from './InstanceListingTable';
import { PaginationControls } from './PaginationControls';
import { useLazyGroupRows } from './use-lazy-group-rows';

const PAGE_LIMIT = 50;
const AUTO_REFRESH_MS = 60_000;

/** Brak nagłówków koszyków (zanim serwer odpowie) — stała tożsamość dla memoizacji podziału. */
const NO_GROUPS: readonly PendingDecisionGroupHeader[] = [];
/** Brak sekcji (tryb inny niż „Kartoteka") — stała tożsamość, propsy sekcji się nie zmieniają. */
const NO_SECTIONS: readonly SectionEntry[] = [];

/**
 * Okno „zbliża się termin" (tona `warning`) — front-endowa heurystyka WIZUALNA
 * kolumny „Termin", NIEZWIĄZANA z progiem eskalacji silnika (ten jest per proces/krok,
 * konfigurowany w builderze). 24h przed terminem → ostrzeżenie; po terminie → danger
 * (`process_instances.deadline_at < now()`, ta sama definicja co filtr `?overdue=true`
 * i kafelek „Przeterminowane").
 */
const DEADLINE_WARNING_WINDOW_MS = 24 * 60 * 60 * 1000;

function deadlineTone(deadlineAt: string): 'danger' | 'warning' | 'neutral' {
  const diffMs = new Date(deadlineAt).getTime() - Date.now();
  if (diffMs <= 0) return 'danger';
  if (diffMs <= DEADLINE_WARNING_WINDOW_MS) return 'warning';
  return 'neutral';
}

/** „za 3 dni" / „wczoraj" / „za 5 godzin" — `Intl.RelativeTimeFormat` (ICU) niesie
 * poprawną polską odmianę bez własnych kluczy i18n pluralizacji (mirror `DatePicker`,
 * który też opiera nazwy miesięcy/dni na `Intl`). Poniżej 24h liczymy w godzinach
 * (precyzyjniej dla zbliżającego się/świeżo minionego terminu), inaczej w dniach. */
function formatDeadlineRelative(deadlineAt: string): string {
  const diffMs = new Date(deadlineAt).getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat('pl', { numeric: 'auto' });
  if (Math.abs(diffMs) < DEADLINE_WARNING_WINDOW_MS) {
    return rtf.format(Math.round(diffMs / (60 * 60 * 1000)), 'hour');
  }
  return rtf.format(Math.round(diffMs / DEADLINE_WARNING_WINDOW_MS), 'day');
}

/**
 * Komórka kolumny „Termin" — data + wskaźnik względny w tonacji zależnej od pilności
 * + odznaka „Eskalowano" (`process_step_instances.escalated`). Brak terminu → `null`
 * (pusta komórka, bez placeholdera „—" — termin jest opcjonalny, nie „brakujący").
 */
function DeadlineCell({ row }: { row: MyPendingDecisionRow }) {
  const { t } = useTranslation();
  if (!row.deadlineAt) return null;
  const tone = deadlineTone(row.deadlineAt);
  return (
    <div
      className="flex flex-col items-start gap-1"
      data-testid={`approvals-deadline-${row.stepInstanceId}`}
    >
      <Badge tone={tone} size="sm">
        {new Date(row.deadlineAt).toLocaleDateString('pl-PL')} ·{' '}
        {formatDeadlineRelative(row.deadlineAt)}
      </Badge>
      {row.escalated && (
        <Badge tone="info" size="sm" data-testid={`approvals-escalated-${row.stepInstanceId}`}>
          {t('tenant.processes.approvals.escalatedBadge')}
        </Badge>
      )}
    </div>
  );
}

/**
 * Komórka kolumn kwotowych („Netto", „Brutto", „Wartość"): kwota sformatowana walutowo albo „—",
 * gdy rekord docelowy nie ma tej kwoty (cel nie jest dokumentem lub klasa bez tego pola).
 */
function MoneyCell({ amount, testId }: { amount: TargetAmount | null; testId: string }) {
  if (!amount) return <span className="text-text-tertiary">—</span>;
  return (
    <span className="tabular-nums" data-testid={testId}>
      {formatMoney(amount.amount, amount.currencyCode)}
    </span>
  );
}

/** Opcja filtra „Firma" (lista rozwijana z wyszukiwaniem; `value` = id firmy). */
interface CompanyFilterOption {
  value: string;
  label: string;
}

/** Kopia opcji filtra „Firma" w polskiej kolejności alfabetycznej etykiet (wejście bez zmian). */
function sortCompanyOptions(options: readonly CompanyFilterOption[]): CompanyFilterOption[] {
  return [...options].sort((a, b) => a.label.localeCompare(b.label, 'pl'));
}

/**
 * Opcje filtra „Firma" z koszyków `groupBy=company`: firmy REKORDÓW, których dotyczą sprawy z
 * bieżącego zakresu. Koszyk `__none__` (rekord bez firmy — możliwy np. dla rekordu usuniętego, a w
 * schemacie dokument bywa bez firmy) pomijamy ŚWIADOMIE: lista rozwijana nie ma pozycji „bez
 * firmy". Etykieta koszyka firmy jest pusta tylko wyjątkowo (np. id firmy spoza tabeli firm, z
 * kopii danych) — wtedy zamiast surowego id pokazujemy `unknownLabel` („(nieznana firma)").
 * Sortowane po etykiecie.
 */
function buildCompanyFilterOptions(
  groups: readonly PendingDecisionGroupHeader[],
  unknownLabel: string,
): CompanyFilterOption[] {
  return sortCompanyOptions(
    groups
      .filter((group) => group.key !== DECISION_GROUP_NONE_KEY)
      .map((group) => ({ value: group.key, label: group.label ?? unknownLabel })),
  );
}

/** Stan filtra „Firma" przekazywany do definicji kolumn. */
interface CompanyFilterState {
  /** Opcje listy rozwijanej (firmy z bieżącego zakresu + wybrane firmy spoza niego). */
  options: CompanyFilterOption[];
  /**
   * Opcji nie udało się wczytać (zapytanie padło i nie ma żadnych wcześniejszych opcji) — nagłówek
   * kolumny dostaje wizualną podpowiedź (dla czytnika ekranu służy osobny region `role="status"`).
   */
  loadFailed: boolean;
}

/**
 * Klucz zapytania o nagłówki koszyków (grupowanie) — JEDNA funkcja dla `groupedQuery` i dla zapytania
 * o opcje filtra „Firma" (`groupBy: 'company'`, bez filtrów): gdy oba są tym samym zapytaniem (grupowanie
 * po firmie bez filtrów), mają wspólny wpis w cache i jedno żądanie. Zaczyna się od `['processes',
 * 'my-pending-decisions']`, więc unieważnienie `['processes']` po decyzji odświeża go razem z listą.
 */
function pendingGroupedKey(view: {
  groupBy: PendingDecisionGroupBy | null;
  headerFilters: string | undefined;
  overdue: boolean;
  scope: PendingDecisionScope;
  companyId: string | undefined;
}) {
  return [
    'processes',
    'my-pending-decisions',
    'grouped',
    {
      groupBy: view.groupBy,
      headerFilters: view.headerFilters,
      overdue: view.overdue,
      scope: view.scope,
      companyId: view.companyId ?? null,
    },
  ] as const;
}

/**
 * Buduje kolumny listingu "Moje czekające". `headerFilter`/`sortable` na kolumnach z pokryciem
 * backendowym (`HEADER_FILTER_FIELDS`/`headerFilterExprFields`/`sortFields` w `route.ts`):
 * surowe kolumny SQL + `process_name`/`submitted_by` (dociągnięte LEFT JOIN-em) + `priority`
 * (surowa kolumna, filtr równościowy) + `amount`/`target_label`/`current_node` — te trzy są
 * wyrażeniami SQL WYLICZANYMI (`AMOUNT_EXPR`/`targetLabel` z `buildRecordExpressions`/
 * `STEP_NODE_LABEL_EXPR`), ale backend filtruje/sortuje je WPROST przez `headerFilterExprFields`/
 * `sortFields` — fala 2 kokpitu decyzji (żadna z nich nie jest już „niefiltrowalna", pierwotny
 * komentarz o braku prawdziwej kolumny Drizzle jest NIEAKTUALNY, patrz historia tego pliku).
 * `conditionOnly: true`
 * WYŁĄCZNIE na kolumnach tekstowych (`target_module_code`/`process_name`/`submitted_by`/
 * `priority`/`target_label`/`current_node`) — zakładka „Wartości" popupu filtra wymaga endpointu
 * `/distinct`, którego procesy nie mają. Kolumny liczba/data (`instance_number`/
 * `instance_submitted_at`) NIE dostają tej flagi: `conditionOnly` gasi RÓWNIEŻ zakładkę „Zakres"
 * (`ColumnHeaderFilterPopup.tsx`: `showRangeTab = !conditionOnly && …`), a zakres dla liczby/daty
 * działa end-to-end (`buildHeaderFiltersSQL` obsługuje `mode:'range'`) — gaszenie go byłoby
 * utratą działającej funkcji, nie sprzątaniem martwej. `current_node` jest sortowalna TYLKO na
 * tym ekranie (krok jest pojedynczy, skalar) — kontrast z ekranami spraw (`InstanceListingTable.tsx`),
 * gdzie krok bywa wielowartościowy i sort jest wyłączony świadomie. Kolumna `target_label` jest
 * tytułem karty (`titleColumnId` niżej), więc w widoku kart nie dubluje się w treści.
 *
 * Kolumny „Kontrahent"/„Firma"/„Netto"/„Brutto" (fala 1 listy decyzji): `counterparty` — tekst,
 * filtr wyłącznie jako warunek (`conditionOnly`, bez zakładki „Wartości"); `net_amount`/
 * `gross_amount` — jak `amount` (liczba: warunek + zakres); `company` — firma REKORDU, którego
 * sprawa dotyczy; filtr to lista rozwijana z wyszukiwaniem (`record-select`, wzorzec
 * `buildCompanyColumn` z list dokumentów) z firmami rekordów z bieżącego zakresu oraz firmami
 * wybranymi w filtrze (`companyFilter.options`, patrz `companyOptions` w komponencie), wartości =
 * id firmy. `record-select` ma kontrolkę WYŁĄCZNIE w wierszu filtrów (bez ikony popupu) i tylko
 * przy niepustych opcjach. `filterable: false` zostaje jako zabezpieczenie (jak w
 * `buildCompanyColumn`), ale tu jest bez efektu: tabela dostaje `headerFilters`, a NIE
 * `columnFilters`, więc `FilterRowCell` i tak zostawia pustą komórkę zamiast legacy pola
 * „Szukaj…". Id kolumny `company` = slug sortu, a `fieldSlug` filtra = `company_id` (filtr po id
 * firmy, nie po nazwie). Kolumna ma `headerFilter` ZAWSZE (nawet przy pustej liście opcji): filtr
 * aktywny w stanie `headerFilters` serializuje się po `fieldSlug` kolumny, a kolumna bez
 * `headerFilter` oddałaby id kolumny (`company`) zamiast sluga `company_id`. Gdy zapytanie o
 * opcje się nie powiodło (`companyFilter.loadFailed`), nagłówek kolumny dostaje krótki wyszarzony
 * komunikat (`renderHeader`) — kontrolki filtra nie da się opisać bez zmiany wspólnej tabeli.
 */
function buildColumns(
  t: TFunction,
  companyFilter: CompanyFilterState,
): ColumnDefinition<MyPendingDecisionRow>[] {
  return [
    {
      id: 'instance_number',
      accessor: 'instanceNumber',
      header: t('tenant.processes.approvals.columns.instanceNumber'),
      defaultWidth: 90,
      sortable: true,
      // `conditionOnly` TYLKO na kolumnach tekstowych w tym pliku — dla number/date ta flaga
      // gasi RÓWNIEŻ zakładkę „Zakres" (`showRangeTab = !conditionOnly && …`,
      // `ColumnHeaderFilterPopup.tsx`), nie tylko martwą „Wartości". Zakres dla liczby/daty
      // działa end-to-end (`buildHeaderFiltersSQL` obsługuje `mode:'range'`) — zostaje.
      headerFilter: { category: 'number', fieldSlug: 'instance_number' },
      renderCell: (_value, row) => <span className="font-mono text-xs">#{row.instanceNumber}</span>,
    },
    {
      id: 'priority',
      accessor: 'priority',
      header: t('tenant.processes.approvals.columns.priority'),
      defaultWidth: 110,
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'priority', conditionOnly: true },
      renderCell: (_value, row) => (
        <PriorityBadge
          priority={row.priority}
          testId={`approvals-priority-${row.stepInstanceId}`}
        />
      ),
    },
    {
      id: 'deadline_at',
      accessor: 'deadlineAt',
      header: t('tenant.processes.approvals.columns.deadline'),
      defaultWidth: 160,
      sortable: true,
      renderCell: (_value, row) => <DeadlineCell row={row} />,
    },
    {
      id: 'process_name',
      accessor: 'processName',
      header: t('tenant.processes.approvals.columns.processName'),
      defaultWidth: 200,
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'process_name', conditionOnly: true },
      renderCell: (_value, row) =>
        row.processName ?? (
          <span className="italic text-text-tertiary">
            {t('tenant.processes.approvals.fallback.missingProcessName')}
          </span>
        ),
    },
    {
      id: 'target_label',
      accessor: 'targetLabel',
      header: t('tenant.processes.approvals.columns.target'),
      defaultWidth: 200,
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'target_label', conditionOnly: true },
      renderCell: (_value, row) =>
        row.targetLabel ?? (
          <span className="italic text-text-tertiary">
            {t('tenant.processes.approvals.fallback.missingTarget')}
          </span>
        ),
    },
    {
      id: 'counterparty',
      accessor: 'counterpartyName',
      header: t('tenant.processes.approvals.columns.counterparty'),
      defaultWidth: 200,
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'counterparty', conditionOnly: true },
      renderCell: (_value, row) =>
        row.counterpartyName ? (
          <span data-testid={`approvals-counterparty-${row.stepInstanceId}`}>
            {row.counterpartyName}
          </span>
        ) : (
          <span className="text-text-tertiary">—</span>
        ),
    },
    {
      id: 'company',
      accessor: 'companyName',
      header: t('tenant.processes.approvals.columns.company'),
      defaultWidth: 160,
      sortable: true,
      // Zabezpieczenie jak w `buildCompanyColumn` (bez legacy pola „Szukaj…" w wierszu filtrów);
      // dziś bez efektu — tabela nie dostaje `columnFilters` (patrz opis funkcji).
      filterable: false,
      headerFilter: {
        category: 'record-select',
        fieldSlug: 'company_id',
        options: companyFilter.options,
      },
      // Wizualna podpowiedź przy nieudanym wczytaniu opcji (`aria-hidden`: czytnik dostaje komunikat
      // z osobnego regionu `role="status"` poza tabelą, `companyOptionsStatus`). Etykieta nagłówka
      // jak domyślna (`config.customLabel` z układu kolumn albo nazwa kolumny), rozmiar jak reszta
      // nagłówka. Klik w podpowiedź nie sortuje kolumny (`stopPropagation` — nagłówek sortuje po
      // kliknięciu całej komórki).
      ...(companyFilter.loadFailed
        ? {
            renderHeader: (config) => (
              <span className="flex flex-col items-start">
                <span>{config.customLabel || t('tenant.processes.approvals.columns.company')}</span>
                <span
                  aria-hidden="true"
                  className="font-normal normal-case tracking-normal text-text-tertiary"
                  onClick={(event) => event.stopPropagation()}
                  data-testid="approvals-company-options-error"
                >
                  {t('tenant.processes.approvals.companyOptionsError')}
                </span>
              </span>
            ),
          }
        : {}),
      renderCell: (_value, row) =>
        row.companyName ? (
          <span data-testid={`approvals-company-${row.stepInstanceId}`}>{row.companyName}</span>
        ) : (
          <span className="text-text-tertiary">—</span>
        ),
    },
    {
      id: 'net_amount',
      accessor: (row) => (row.netAmount ? row.netAmount.amount : null),
      header: t('tenant.processes.approvals.columns.net'),
      defaultWidth: 130,
      sortable: true,
      headerFilter: { category: 'number', fieldSlug: 'net_amount' },
      renderCell: (_value, row) => (
        <MoneyCell amount={row.netAmount} testId={`approvals-net-${row.stepInstanceId}`} />
      ),
    },
    {
      id: 'gross_amount',
      accessor: (row) => (row.grossAmount ? row.grossAmount.amount : null),
      header: t('tenant.processes.approvals.columns.gross'),
      defaultWidth: 130,
      sortable: true,
      headerFilter: { category: 'number', fieldSlug: 'gross_amount' },
      renderCell: (_value, row) => (
        <MoneyCell amount={row.grossAmount} testId={`approvals-gross-${row.stepInstanceId}`} />
      ),
    },
    {
      id: 'amount',
      // Sortowalna I filtrowalna server-side (`AMOUNT_EXPR` w route.ts, przez `SORT_FIELDS` +
      // `HEADER_FILTER_EXPR_FIELDS`) — kwota jest wyrażeniem CASE (klasa dokumentu decyduje o
      // polu), nie pojedynczą kolumną Drizzle, ale backend filtruje/sortuje wyrażenia tak samo
      // jak zwykłe kolumny (fala 2 kokpitu decyzji). Wiersze bez kwoty (cel nie jest dokumentem
      // lub klasa bez kwoty) dają `NULL` w wyrażeniu — porównania numeryczne po prostu ich nie
      // łapią, zgodnie z zasadą „nie porzucamy filtra". „Netto"/„Brutto" (kolumny wyżej) mają
      // ten sam mechanizm (`net_amount`/`gross_amount`).
      accessor: (row) => (row.amount ? row.amount.amount : null),
      header: t('tenant.processes.approvals.columns.amount'),
      defaultWidth: 130,
      sortable: true,
      headerFilter: { category: 'number', fieldSlug: 'amount' },
      renderCell: (_value, row) => (
        <MoneyCell amount={row.amount} testId={`approvals-amount-${row.stepInstanceId}`} />
      ),
    },
    {
      id: 'target_module_code',
      accessor: 'targetModuleCode',
      header: t('tenant.processes.approvals.columns.module'),
      defaultWidth: 160,
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'target_module_code', conditionOnly: true },
      renderCell: (_value, row) => (
        <span className="text-text-tertiary">{t(moduleLabelI18nKey(row.targetModuleCode))}</span>
      ),
    },
    {
      id: 'current_node',
      accessor: 'currentNodeLabel',
      header: t('tenant.processes.approvals.columns.currentNode'),
      defaultWidth: 200,
      // Sortowalna TYLKO tutaj — na „Moje czekające" krok jest pojedynczy (skalar ze snapshotu,
      // `STEP_NODE_LABEL_EXPR`). Ekrany spraw (ziarno instancji, `InstanceListingTable.tsx`) mają
      // ten sam filtr, ale NIE sort — tam krok bywa wielowartościowy (parallel).
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'current_node', conditionOnly: true },
      renderCell: (_value, row) =>
        row.currentNodeLabel ?? (
          <span className="text-text-tertiary">
            {t('tenant.processes.approvals.fallback.missingNode')}
          </span>
        ),
    },
    {
      id: 'submitted_by',
      accessor: (row) => (row.submittedByUser ? displaySubmitter(row.submittedByUser) : null),
      header: t('tenant.processes.approvals.columns.submittedBy'),
      defaultWidth: 180,
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'submitted_by', conditionOnly: true },
      renderCell: (_value, row) =>
        row.submittedByUser ? (
          <span className="text-text-tertiary">{displaySubmitter(row.submittedByUser)}</span>
        ) : (
          <span className="italic text-text-tertiary">
            {t('tenant.processes.approvals.fallback.missingSubmitter')}
          </span>
        ),
    },
    {
      id: 'submitted_at',
      accessor: 'instanceSubmittedAt',
      header: t('tenant.processes.approvals.columns.submittedAt'),
      defaultWidth: 140,
      sortable: true,
      // `conditionOnly` pominięte celowo — patrz komentarz przy kolumnie `instance_number`.
      headerFilter: { category: 'date', fieldSlug: 'instance_submitted_at' },
      renderCell: (_value, row) => (
        <span className="text-xs text-text-tertiary">
          {new Date(row.instanceSubmittedAt).toLocaleString('pl-PL')}
        </span>
      ),
    },
  ];
}

/**
 * Dzieli wynik akcji masowej na zaakceptowane (`done` + `saved_waiting` — bez rozróżnienia) i
 * pominięte (`failed` + `cancelled`, z powodem każdej). Idzie po WYSŁANYCH wierszach, nie po
 * `results`: pozycja bez wyniku (serwer zwraca po jednym na pozycję, więc to naruszenie kontraktu)
 * liczy się jako pominięta z ogólnym powodem — nigdy jako zaakceptowana.
 *
 * `keepSelectedIds` — pominięte sprawy, które mają ZOSTAĆ zaznaczone (do rozstrzygnięcia
 * pojedynczo): wszystkie pominięte poza anulowanymi i tymi, których rekord usunięto
 * (`TARGET_RECORD_DELETED` — sprawa „nie istnieje" do przywrócenia kartoteki) albo z nieaktywnym
 * modułem kartoteki (`TARGET_MODULE_INACTIVE` — serwer ukrywa taką sprawę na listach), bo wszystkie
 * znikają z listy po odświeżeniu i nie ma czego zaznaczać.
 */
function splitBulkOutcome(
  t: TFunction,
  sentRows: readonly MyPendingDecisionRow[],
  response: BulkDecisionResponse,
): { accepted: number; skipped: BulkSkippedItem[]; keepSelectedIds: string[] } {
  const resultByStep = new Map<string, BulkDecisionResultItem>(
    response.results.map((result) => [result.stepInstanceId, result]),
  );
  let accepted = 0;
  const skipped: BulkSkippedItem[] = [];
  const keepSelectedIds: string[] = [];
  for (const row of sentRows) {
    const result: BulkDecisionResultItem = resultByStep.get(row.stepInstanceId) ?? {
      stepInstanceId: row.stepInstanceId,
      status: 'failed',
    };
    if (result.status === 'done' || result.status === 'saved_waiting') {
      accepted += 1;
      continue;
    }
    skipped.push({
      stepInstanceId: row.stepInstanceId,
      instanceNumber: row.instanceNumber,
      targetLabel: row.targetLabel,
      reason: bulkSkipReason(t, result),
    });
    if (
      result.status !== 'cancelled' &&
      result.error !== TARGET_RECORD_DELETED_CODE &&
      result.error !== TARGET_MODULE_INACTIVE_CODE
    ) {
      keepSelectedIds.push(row.stepInstanceId);
    }
  }
  return { accepted, skipped, keepSelectedIds };
}

/**
 * Wiersze bez powtórzeń po id kroku (pierwsze wystąpienie wygrywa, kolejność zachowana). Krok nie
 * powinien być jednocześnie w tabeli i w sekcji, ale akcja masowa wysyła każdy krok dokładnie raz,
 * więc zbiór jest zabezpieczony.
 */
function uniqueByStepId(rows: readonly MyPendingDecisionRow[]): MyPendingDecisionRow[] {
  const byId = new Map<string, MyPendingDecisionRow>();
  for (const row of rows) {
    if (!byId.has(row.stepInstanceId)) byId.set(row.stepInstanceId, row);
  }
  return Array.from(byId.values());
}

export function MyPendingDecisionsTab() {
  const { t } = useTranslation();
  const navigateToTarget = useNavigateToTarget();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [offset, setOffset] = useState(0);
  // Id zaznaczonych wierszy (stan surowy). Do akcji i do tabeli idzie `selectedIds` liczone niżej
  // jako jego część wspólna z wierszami wczytanymi na ekranie.
  const [markedIds, setMarkedIds] = useState<string[]>([]);
  // Kroki wczytane w sekcjach kartotek (tryb sekcji) — rejestr wg klucza sekcji, zasilany przez same
  // sekcje (`registerSectionSteps`); liczy się do zaznaczenia razem z wierszami tabeli pozostałych.
  const [sectionSteps, setSectionSteps] = useState<
    ReadonlyMap<string, readonly MyPendingDecisionRow[]>
  >(() => new Map());
  // Tryb widoku „Lista / Karty" — JEDEN dla zwykłej listy i dla sekcji kartotek (patrz opis pliku).
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  // Pytanie „Zaakceptować N decyzji drogą domyślną?" (akcja masowa).
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  // Okno wyniku akcji masowej — tylko gdy coś pominięto (pełny sukces = sam dymek).
  const [bulkResult, setBulkResult] = useState<{
    accepted: number;
    skipped: BulkSkippedItem[];
  } | null>(null);
  // Wiersz z otwartym oknem „Akceptacja" (decyzja per sprawa) + cel z klikniętego przycisku: droga
  // (`routeKey: null` = „Akceptacja", czyli droga domyślna) albo cofnięcie z menu „…".
  const [decision, setDecision] = useState<{
    row: MyPendingDecisionRow;
    target: DecisionTarget;
  } | null>(null);
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [autoRefresh, setAutoRefresh] = useState(true);
  // Zakres „Zespół" — WARTOŚĆ POCHODNA z URL (`?scope=team`), NIE osobny stan. Klik kafelka
  // „Zespół" w ApprovalsPage przelicza to natychmiast (bez remountu, gdy jesteśmy już na tej
  // zakładce). `readOnly` wyłącza przyciski decyzji, multi-select i pasek masowych akcji — widok
  // podwładnych jest TYLKO-PODGLĄD (zostaje „Otwórz formularz").
  const scope: PendingDecisionScope = searchParams.get('scope') === 'team' ? 'team' : 'mine';
  // Firma z górnego paska: konkretna firma zawęża decyzje do spraw z rekordami tej firmy, tryb
  // „Wszystkie firmy" pokazuje wszystkie (jak pozostałe listy). Wchodzi do kluczy zapytań, więc
  // zmiana firmy odświeża listę, koszyki i opcje filtra „Firma".
  const activeCompanyId = companyIdParam(useActiveCompanyScope());
  const readOnly = scope === 'team';
  // Filtr „tylko przeterminowane" — WARTOŚĆ POCHODNA z URL (`?overdue=true`), NIE osobny stan.
  // Dzięki temu klik kafelka „Przeterminowane" w ApprovalsPage (zmiana URL bez remountu, gdy
  // jesteśmy już na tej zakładce) natychmiast przelicza filtr i queryKey — bez desyncu
  // przełącznik↔URL↔zapytanie. W zakresie „Zespół" filtr jest ignorowany (spójnie z gaszeniem
  // wyróżnienia kafelka „Przeterminowane" na ApprovalsPage), więc `?scope=team&overdue=true`
  // z ręcznego URL nie łączy obu filtrów.
  const overdueOnly = scope !== 'team' && searchParams.get('overdue') === 'true';
  // Grupowanie server-side (fala 2 kokpitu decyzji) — WARTOŚĆ POCHODNA z URL
  // (`?groupBy=<wymiar>`), NIE osobny stan — mirror `scope`/`overdueOnly` wyżej.
  // Nieznana/pusta wartość → `null` (bez grupowania, dotychczasowe zachowanie).
  // Ortogonalne do `scope`/`overdueOnly` — backend filtruje, potem grupuje. Biała lista
  // `PENDING_DECISION_GROUP_BY` (NIE pełny `DECISION_GROUP_BY`) — ten ekran nie oferuje
  // np. `status` (backend go tu odrzuca, walidator `MyPendingDecisionsQuerySchema.groupBy`).
  const groupBy: PendingDecisionGroupBy | null = parsePendingGroupBy(searchParams.get('groupBy'));
  const isGrouped = groupBy !== null;

  // „Pokolenie widoku": licznik zwiększany przy KAŻDEJ zmianie widoku — zakresu (`scope`), filtra
  // „przeterminowane" lub wymiaru grupowania (w tym wejścia/wyjścia z trybu sekcji i z grupowania do
  // listy płaskiej). Wszystko to wartości pochodne z URL, więc zmiana przychodzi też z kafelków i
  // linków, nie tylko z selektora — stąd jeden klucz widoku zamiast wołania w `handleGroupByChange`
  // (wzorzec „dopasuj stan do zmienionej wartości w trakcie renderu"). Pokolenie rozstrzyga, czy
  // dane-zastępnik (`placeholderData`) w ogóle wolno pokazać: `previousQuery` w react-query przeżywa
  // okres `enabled: false`, więc sam klucz zapytania nie odróżni powrotu z grupowania do listy płaskiej
  // (ani do wcześniej używanego wymiaru) od zwykłej zmiany filtra w tym samym widoku.
  const viewKey = `${scope}|${String(overdueOnly)}|${groupBy ?? ''}`;
  const [prevViewKey, setPrevViewKey] = useState(viewKey);
  const [viewGeneration, setViewGeneration] = useState(0);
  if (prevViewKey !== viewKey) {
    setPrevViewKey(viewKey);
    setViewGeneration((generation) => generation + 1);
  }
  // Pokolenie, w którym powstała OSTATNIA PRAWDZIWA odpowiedź (nie dane-zastępnik) — osobno dla
  // listy płaskiej i grupowania; ustawiane niżej, po zapytaniach.
  const [flatDataGeneration, setFlatDataGeneration] = useState<number | null>(null);
  const [groupedDataGeneration, setGroupedDataGeneration] = useState<number | null>(null);

  // Zmiana zakresu lub kafelka („Przeterminowane" / „Zespół") zmienia zbiór wierszy pod
  // zaznaczeniem — czyścimy je (jak przy zmianie strony, filtra, sortowania i grupowania).
  // Kafelki zmieniają tylko URL bez remountu zakładki, więc samo „odmontowanie" tego nie załatwi.
  // Wzorzec „dopasuj stan do zmienionej wartości w trakcie renderu" — bez klatki z cudzym zaznaczeniem.
  const selectionScopeKey = `${scope}|${String(overdueOnly)}`;
  const [prevSelectionScopeKey, setPrevSelectionScopeKey] = useState(selectionScopeKey);
  if (prevSelectionScopeKey !== selectionScopeKey) {
    setPrevSelectionScopeKey(selectionScopeKey);
    setMarkedIds([]);
  }

  // Tryb sekcji kartotek: „Grupuj według: Kartoteka" (we wszystkich zakresach, także „Zespół" — tam
  // sekcje są tylko do odczytu). Tabela POZOSTAŁYCH kartotek nie ma w nim filtrów nagłówka ani
  // sortowania (filtry i sortowanie mają same sekcje), więc KAŻDE wejście w ten tryb — selektor
  // grupowania, link z `?groupBy=target_class` — czyści filtry i sortowanie wspólnej tabeli (wzorzec
  // „dopasuj stan do zmienionej wartości w trakcie renderu", jak `selectionScopeKey`). Dzięki temu
  // zapytanie o nagłówki koszyków nie niesie `headerFilters`, a liczniki sekcji nie zależą od filtra
  // tabeli.
  const sectionsMode = groupBy === 'target_class';
  const [prevSectionsMode, setPrevSectionsMode] = useState(sectionsMode);
  if (prevSectionsMode !== sectionsMode) {
    setPrevSectionsMode(sectionsMode);
    if (sectionsMode) {
      setHeaderFilters({});
      setSortColumn(null);
      setSortDirection('asc');
      setOffset(0);
    }
  }

  // Auto-odświeżanie pauzuje przy otwartym oknie decyzji / pytaniu / wyniku akcji masowej lub
  // aktywnym zaznaczeniu — żeby lista nie „uciekła" pod ręką w trakcie decydowania.
  // „Aktywne zaznaczenie" = choć jeden zaznaczony wiersz jest WCZYTANY na ekranie (patrz
  // `selectedIds` niżej), a nie sam niepusty `markedIds`: id sprawy, której już nie ma na liście
  // (anulowana, rozstrzygnięta gdzie indziej), nie może wstrzymywać odświeżania na zawsze.
  // Wiersze dostarczają zapytania skonfigurowane tą właśnie pauzą, więc flaga jest stanem
  // dopasowywanym w trakcie renderu po obliczeniu `selectedIds` (wzorzec jak `selectionScopeKey`).
  const [hasLoadedSelection, setHasLoadedSelection] = useState(false);
  const dialogOpen = decision !== null || bulkConfirmOpen || bulkResult !== null;
  const pauseAuto = dialogOpen || hasLoadedSelection;

  // Interwał odświeżania zapytań grupujących (nagłówki koszyków, opcje filtra „Firma"): w rytmie listy
  // (auto-odświeżanie i jego pauza), ale po 403 (brak uprawnienia) bez dalszego odpytywania — to się
  // nie zmieni samo; odświeżanie wróci po zmianie widoku (inny klucz zapytania).
  const groupedRefetchInterval = (query: { state: { error: unknown } }): number | false =>
    autoRefresh && !pauseAuto && !isForbiddenError(query.state.error) ? AUTO_REFRESH_MS : false;

  // Opcje filtra „Firma": firmy rekordów spraw z bieżącego zakresu (`overdue`/`scope`), liczone osobnym
  // zapytaniem o koszyki `groupBy=company` BEZ filtrów nagłówka — opcje nie mogą znikać po zawężeniu
  // listy filtrem. Klucz z `pendingGroupedKey` (ta sama funkcja co `groupedQuery`): przy
  // `?groupBy=company` bez filtrów to JEDNO zapytanie dla obu. Ponawianie jak w `groupedQuery`
  // (`retryUnlessForbidden`). W trybie sekcji kolumny nie mają filtrów (patrz `tableColumns`), więc
  // zapytania nie ma.
  const companyOptionsQuery = useQuery({
    queryKey: pendingGroupedKey({
      groupBy: 'company',
      headerFilters: undefined,
      overdue: overdueOnly,
      scope,
      companyId: activeCompanyId,
    }),
    queryFn: () =>
      fetchGroupedPendingDecisions({
        groupBy: 'company',
        overdue: overdueOnly,
        scope: scope === 'team' ? 'team' : undefined,
        companyId: activeCompanyId,
      }),
    enabled: !sectionsMode,
    refetchInterval: groupedRefetchInterval,
    retry: retryUnlessForbidden,
  });
  const companyGroups = companyOptionsQuery.data?.groups;
  const unknownCompanyLabel = t('tenant.processes.approvals.companyUnknown');
  const companyOptionsInScope = useMemo(
    () => buildCompanyFilterOptions(companyGroups ?? [], unknownCompanyLabel),
    [companyGroups, unknownCompanyLabel],
  );
  // Pamięć etykiet firm widzianych w opcjach (życie komponentu): firma wybrana w filtrze, której nie
  // ma w opcjach NOWEGO zakresu (zmiana kafelka), musi zostać widoczna i usuwalna — dopinamy ją do
  // opcji z zapamiętaną etykietą (a gdy jej nie znamy: „(nieznana firma)"). Pamięć to stan
  // aktualizowany w trakcie renderu po zmianie opcji zakresu (wzorzec jak `selectionScopeKey`),
  // dzięki czemu `companyOptions` jest czystym `useMemo`.
  const [companyLabels, setCompanyLabels] = useState<ReadonlyMap<string, string>>(() => new Map());
  const [prevCompanyOptionsInScope, setPrevCompanyOptionsInScope] = useState(companyOptionsInScope);
  if (prevCompanyOptionsInScope !== companyOptionsInScope) {
    setPrevCompanyOptionsInScope(companyOptionsInScope);
    setCompanyLabels((previous) => {
      const next = new Map(previous);
      for (const option of companyOptionsInScope) next.set(option.value, option.label);
      return next;
    });
  }
  const companyFilterValue = headerFilters.company;
  const selectedCompanyIds = useMemo(
    () =>
      companyFilterValue?.mode === 'values'
        ? (companyFilterValue.selectedValues ?? []).filter(
            (value): value is string => value !== null,
          )
        : [],
    [companyFilterValue],
  );
  const companyOptions = useMemo(() => {
    const inScope = new Set(companyOptionsInScope.map((option) => option.value));
    const selectedOutside = selectedCompanyIds
      .filter((id) => !inScope.has(id))
      .map((id) => ({ value: id, label: companyLabels.get(id) ?? unknownCompanyLabel }));
    return selectedOutside.length === 0
      ? companyOptionsInScope
      : sortCompanyOptions([...companyOptionsInScope, ...selectedOutside]);
  }, [companyOptionsInScope, selectedCompanyIds, companyLabels, unknownCompanyLabel]);

  // Komunikat o błędzie tylko, gdy opcji NIE MA (zapytanie padło, brak wcześniejszej odpowiedzi): przy
  // nieudanym ODŚWIEŻENIU istniejących opcji lista działa na ostatnich znanych danych, więc komunikat
  // byłby fałszywy. Ten sam warunek zasila podpowiedź w nagłówku i region dla czytnika.
  const companyLoadFailed =
    companyOptionsQuery.isError && companyOptionsQuery.data === undefined && !sectionsMode;
  const columns = useMemo(
    () => buildColumns(t, { options: companyOptions, loadFailed: companyLoadFailed }),
    [t, companyOptions, companyLoadFailed],
  );
  // Tabela pozostałych kartotek w trybie sekcji jest bez filtrów nagłówka i bez sortowania: kolumny
  // tracą `headerFilter` i sortowalność (patrz niżej `filterable={!sectionsMode}`). `columns` (pełne)
  // służą dalej do serializacji filtrów, które w tym trybie są puste.
  const tableColumns = useMemo(
    () =>
      sectionsMode
        ? columns.map((column) => ({ ...column, sortable: false, headerFilter: undefined }))
        : columns,
    [columns, sectionsMode],
  );
  const serializedHeaderFilters = useMemo(
    () => serializeHeaderFiltersForRequest(headerFilters, columns),
    [headerFilters, columns],
  );
  const sortParam = sortColumn ? `${sortColumn}:${sortDirection}` : undefined;

  // Tryb płaski (paginowany, dotychczasowy) — aktywny wyłącznie bez `groupBy`. Zmiana filtra,
  // sortowania lub strony zostawia poprzednie wiersze do czasu odpowiedzi (`placeholderData`), ale
  // TYLKO w tym samym widoku: dane-zastępnik wolno pokazać wyłącznie, gdy ostatnia prawdziwa
  // odpowiedź tego zapytania powstała w BIEŻĄCYM pokoleniu widoku (`flatDataGeneration`). Po zmianie
  // widoku (kafelek, grupowanie, powrót z grupowania do listy) pełne „Ładowanie", żeby nie pokazać
  // wierszy cudzego zakresu ani sprzed grupowania.
  const flatQuery = useQuery({
    queryKey: [
      'processes',
      'my-pending-decisions',
      {
        offset,
        limit: PAGE_LIMIT,
        headerFilters: serializedHeaderFilters,
        sort: sortParam ?? null,
        overdue: overdueOnly,
        scope,
        companyId: activeCompanyId,
      },
    ],
    placeholderData: (previousData) =>
      flatDataGeneration === viewGeneration ? previousData : undefined,
    queryFn: () =>
      fetchMyPendingDecisions({
        limit: PAGE_LIMIT,
        offset,
        headerFilters: serializedHeaderFilters,
        sort: sortParam,
        overdue: overdueOnly,
        scope: scope === 'team' ? 'team' : undefined,
        companyId: activeCompanyId,
      }),
    enabled: !isGrouped,
    refetchInterval: autoRefresh && !pauseAuto && !isGrouped ? AUTO_REFRESH_MS : false,
  });

  // Tryb grupowany (server-side, `?groupBy=`) — aktywny wyłącznie gdy `groupBy` obecny.
  // BEZ paginacji (patrz `fetchGroupedPendingDecisions`) — `offset`/`sort` celowo pominięte.
  // Poprzednie nagłówki zostają do czasu odpowiedzi tylko w tym samym widoku (pokolenie widoku, patrz
  // `groupedDataGeneration`) — jak w `flatQuery`; powrót do wcześniej używanego wymiaru po przerwie
  // (lista płaska, inny wymiar) to już NOWE pokolenie. Ponawianie jak w zapytaniu o opcje
  // (`retryUnlessForbidden`), klucz z tej samej funkcji (`pendingGroupedKey`).
  const groupedQuery = useQuery({
    queryKey: pendingGroupedKey({
      groupBy,
      headerFilters: serializedHeaderFilters,
      overdue: overdueOnly,
      scope,
      companyId: activeCompanyId,
    }),
    placeholderData: (previousData) =>
      groupedDataGeneration === viewGeneration ? previousData : undefined,
    retry: retryUnlessForbidden,
    queryFn: () => {
      // Strażnik TS — nieosiągalny w runtime (`enabled: isGrouped` gwarantuje `groupBy !== null`
      // w momencie wywołania), ale narusza narrowing bez `!`.
      if (groupBy === null) throw new Error('groupedQuery wywołane bez groupBy');
      return fetchGroupedPendingDecisions({
        groupBy,
        headerFilters: serializedHeaderFilters,
        overdue: overdueOnly,
        scope: scope === 'team' ? 'team' : undefined,
        companyId: activeCompanyId,
      });
    },
    enabled: isGrouped,
    refetchInterval: isGrouped ? groupedRefetchInterval : false,
  });

  // Pokolenie ostatniej PRAWDZIWEJ odpowiedzi (nie danych-zastępnika). Zapytanie wyłączone w trakcie
  // innego widoku też może zapisać tu starsze pokolenie (ma w cache swoje dane), ale powrót do jego
  // widoku zawsze zaczyna NOWE pokolenie, więc taki zapis nigdy nie pozwoli pokazać starych danych.
  if (
    flatQuery.isSuccess &&
    !flatQuery.isPlaceholderData &&
    flatDataGeneration !== viewGeneration
  ) {
    setFlatDataGeneration(viewGeneration);
  }
  if (
    groupedQuery.isSuccess &&
    !groupedQuery.isPlaceholderData &&
    groupedDataGeneration !== viewGeneration
  ) {
    setGroupedDataGeneration(viewGeneration);
  }
  // Wiersze/nagłówki na ekranie pochodzą z poprzedniego zapytania (dane-zastępnik): tabela jest wtedy
  // przyciemniona i oznaczona `aria-busy`, a zaznaczanie i akcja masowa — wyłączone (patrz niżej).
  const showingPlaceholder = isGrouped
    ? groupedQuery.isPlaceholderData
    : flatQuery.isPlaceholderData;

  const isLoading = isGrouped ? groupedQuery.isLoading : flatQuery.isLoading;
  const isError = isGrouped ? groupedQuery.isError : flatQuery.isError;
  const currentError = isGrouped ? groupedQuery.error : flatQuery.error;

  const rows = useMemo(() => flatQuery.data?.data ?? [], [flatQuery.data]);
  const total = flatQuery.data?.total ?? 0;
  const sumsByCurrency = flatQuery.data?.sumsByCurrency ?? [];
  const groups: readonly PendingDecisionGroupHeader[] = groupedQuery.data?.groups ?? NO_GROUPS;
  const groupedTotalRows = groupedQuery.data?.total ?? 0;

  // Sekcje kartotek (tryb `sectionsMode`, patrz wyżej): kartoteki z zestawem sekcji idą do osobnych
  // sekcji, a TYLKO pozostałe nagłówki do wspólnej tabeli (`tableGroups`) — też do leniwego
  // doładowania wierszy niżej, żeby tabela nie dociągała wierszy grup, które ma sekcja. Podział
  // jest memoizowany po nagłówkach (te same dane → te same wpisy sekcji): zakładka renderuje się
  // często (zaznaczanie, auto-odświeżanie), a nowe wpisy przy każdym renderze trafiałyby do
  // tabel sekcji jako nowe propsy.
  const sectionSplit = useMemo(
    () => (sectionsMode ? splitSectionGroups(groups) : null),
    [sectionsMode, groups],
  );
  const sections = sectionSplit?.sections ?? NO_SECTIONS;
  const tableGroups: readonly PendingDecisionGroupHeader[] = sectionSplit
    ? sectionSplit.rest
    : groups;

  // Wiersze rozwiniętych koszyków — LENIWE doładowanie (fala 2), wyciągnięte do WSPÓLNEGO
  // haka `useLazyGroupRows` (mirror `ActiveInstancesTab.tsx`/`MySubmittedInstancesTab.tsx`).
  // `queryKeyExtra` MUSI mirror'ować dokładnie to, co `fetchRows` czyta z domknięcia
  // (`serializedHeaderFilters`/`overdueOnly`/`scope`) — inaczej cache react-query się
  // rozjedzie. `buildSummary` — TYLKO ten ekran ma kolumnę „Wartość" (suma per waluta,
  // `formatMoney`, ta sama co pasek „Wartość na liście"); pozostałe dwa ekrany pomijają
  // ten parametr (`summary: null`).
  const { expandedGroupKeys, toggleGroupExpand, resetExpansion, mappedGroups } =
    useLazyGroupRows<MyPendingDecisionRow>({
      groupBy,
      groups: tableGroups,
      enabled: isGrouped,
      queryKeyBase: ['processes', 'my-pending-decisions', 'group-rows'],
      queryKeyExtra: {
        headerFilters: serializedHeaderFilters,
        overdue: overdueOnly,
        scope,
        companyId: activeCompanyId,
        sort: sortParam ?? null,
      },
      fetchRows: ({ groupKey, offset: rowsOffset, limit: rowsLimit }) => {
        // Strażnik TS — nieosiągalny w runtime (`enabled: isGrouped` gwarantuje
        // `groupBy !== null` w momencie faktycznego wywołania przez react-query), ale
        // narusza narrowing bez `!`.
        if (groupBy === null) throw new Error('fetchRows (group-rows) wywołane bez groupBy');
        return fetchPendingDecisionGroupRows({
          groupBy,
          groupKey,
          limit: rowsLimit,
          offset: rowsOffset,
          headerFilters: serializedHeaderFilters,
          overdue: overdueOnly,
          scope: scope === 'team' ? 'team' : undefined,
          companyId: activeCompanyId,
          sort: sortParam,
        });
      },
      buildSummary: (g) =>
        g.sumsByCurrency.length === 0 ? null : (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
            {g.sumsByCurrency.map((s) => (
              <span key={s.currencyCode} className="tabular-nums">
                {formatMoney(s.total, s.currencyCode)}
              </span>
            ))}
          </span>
        ),
      testIdPrefix: 'approvals',
    });

  // Zmiana zakresu („Zespół" ↔ „Moje czekające") lub trybu sekcji zmienia zbiór grup tabeli: grupa
  // rozwinięta wcześniej (np. faktury w „Zespole"), a teraz obsługiwana przez sekcję, nie może być
  // dalej pobierana w ukryciu — rozwinięcie jest kluczowane samym kluczem grupy i przeżyłoby zmianę.
  // Czyścimy je w trakcie renderu (wzorzec jak `selectionScopeKey`).
  const expansionScopeKey = `${scope}|${String(sectionsMode)}`;
  const [prevExpansionScopeKey, setPrevExpansionScopeKey] = useState(expansionScopeKey);
  if (prevExpansionScopeKey !== expansionScopeKey) {
    setPrevExpansionScopeKey(expansionScopeKey);
    resetExpansion();
  }

  const groupByOptions = useMemo(
    () => [
      { value: '', label: t('tenant.processes.approvals.groupBy.none') },
      ...PENDING_DECISION_GROUP_BY.map((dim) => ({
        value: dim,
        label: t(`tenant.processes.approvals.groupBy.${dim}`),
      })),
    ],
    [t],
  );

  function handleGroupByChange(value: string) {
    setMarkedIds([]);
    setOffset(0);
    // Zmiana wymiaru (lub wyjście z grupowania) czyści stan rozwinięcia/paginacji koszyków
    // (`useLazyGroupRows.resetExpansion`) — klucze koszyków są SPECYFICZNE dla wymiaru (id
    // procesu vs enum priorytetu vs…), więc zostawiony stan z poprzedniego wymiaru byłby
    // martwy (i teoretycznie mógłby kolidować, gdyby dwa wymiary wylosowały identyczny
    // string klucza).
    resetExpansion();
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (value) {
          nextParams.set('groupBy', value);
        } else {
          nextParams.delete('groupBy');
        }
        return nextParams;
      },
      { replace: true },
    );
  }

  // Furtka wyjścia z pustego stanu „?overdue=true + zero wyników" — jedyna droga
  // wyłączenia filtra przeterminowanych od usunięcia przełącznika z toolbara
  // (redundantnego z kaflem „Przeterminowane" w ApprovalsPage, który tylko WŁĄCZA
  // filtr, nigdy go nie wyłącza). Bez tego user utykał na ogólnym komunikacie
  // ConfigurableTable („Nie masz aktualnie żadnych czekających decyzji" — ta tabela
  // NIE wie nic o `overdueOnly`, więc nie pokazuje własnego „Wyczyść filtry"),
  // z jedyną ucieczką „przewiń do góry i kliknij inny kafelek".
  function clearOverdueFilter() {
    setMarkedIds([]);
    setOffset(0);
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('overdue');
        return nextParams;
      },
      { replace: true },
    );
  }

  // Sekcja kartotek zgłasza tu kroki, które ma wczytane i pokazane (`null` = żadnych: zwinięcie,
  // błąd, dane zastępcze). Nowe zgłoszenie zastępuje poprzednie; to samo zgłoszenie (ta sama
  // referencja) i wypisanie nieobecnej sekcji nie zmieniają stanu, więc nie ma zbędnych renderów.
  const registerSectionSteps = useCallback(
    (sectionKey: string, steps: readonly MyPendingDecisionRow[] | null) => {
      setSectionSteps((previous) => {
        if (steps === null) {
          if (!previous.has(sectionKey)) return previous;
          const next = new Map(previous);
          next.delete(sectionKey);
          return next;
        }
        if (previous.get(sectionKey) === steps) return previous;
        return new Map(previous).set(sectionKey, steps);
      });
    },
    [],
  );
  // Zmiana zaznaczenia w JEDNEJ tabeli trybu sekcji (sekcja albo tabela pozostałych): podmienia tylko
  // jej wycinek, resztę zaznaczenia zostawia (patrz `replaceSelectionSlice`). Gdy podmiana niczego nie
  // zmienia (np. sekcja zdejmuje swoje kroki przy zwinięciu, a zakładka już wyczyściła zaznaczenie),
  // stan zostaje ten sam — bez zbędnego renderu.
  const handleSelectionSliceChange = useCallback(
    (ownStepIds: readonly string[], nextStepIds: readonly string[]) => {
      setMarkedIds((previous) => {
        const next = replaceSelectionSlice(previous, ownStepIds, nextStepIds);
        return next.length === previous.length && next.every((id, index) => id === previous[index])
          ? previous
          : next;
      });
    },
    [],
  );

  // Wiersze wczytane na ekranie: lista płaska ALBO wiersze rozwiniętych koszyków (`mappedGroups`
  // — ta sama struktura, którą dostaje `ConfigurableTable.groups`) — to są wiersze TABELI; w trybie
  // sekcji dochodzą do nich kroki wczytanych sekcji (rejestr `sectionSteps`). Zwinięty koszyk niesie
  // `rows: []`, więc jego wiersze nie wchodzą ani do zaznaczenia, ani do akcji.
  const tableRows: MyPendingDecisionRow[] = isGrouped
    ? (mappedGroups ?? []).flatMap((group) => group.rows)
    : rows;
  const loadedRows: MyPendingDecisionRow[] = sectionsMode
    ? uniqueByStepId([...tableRows, ...Array.from(sectionSteps.values()).flat()])
    : tableRows;
  const markedIdSet = useMemo(() => new Set(markedIds), [markedIds]);
  const selectedRows = loadedRows.filter((row) => markedIdSet.has(row.stepInstanceId));
  const selectedIds = selectedRows.map((row) => row.stepInstanceId);
  // Do tabeli idzie tylko zaznaczenie JEJ wierszy (w trybie sekcji reszta należy do sekcji) —
  // inaczej nagłówkowy checkbox tabeli porównywałby cudzą liczbę zaznaczeń z liczbą swoich wierszy.
  const tableStepIds = tableRows.map((row) => row.stepInstanceId);
  const tableStepIdSet = new Set(tableStepIds);
  const tableSelectedIds = selectedIds.filter((id) => tableStepIdSet.has(id));
  // Udział sekcji we wspólnym zaznaczeniu: tylko zakres „Moje" (w „Zespole" sekcje są do odczytu),
  // a na danych zastępczych wyłączony — jak na liście (checkboxy znikają do czasu odpowiedzi).
  const sectionSelection = useMemo<DecisionSectionSelection | undefined>(
    () =>
      readOnly
        ? undefined
        : {
            enabled: !showingPlaceholder,
            markedStepIds: markedIdSet,
            onSliceChange: handleSelectionSliceChange,
            onLoadedStepsChange: registerSectionSteps,
          },
    [readOnly, showingPlaceholder, markedIdSet, handleSelectionSliceChange, registerSectionSteps],
  );
  // Pauza auto-odświeżania liczy się z wczytanych zaznaczonych wierszy (patrz `pauseAuto` wyżej).
  const loadedSelectionNow = selectedIds.length > 0;
  if (hasLoadedSelection !== loadedSelectionNow) setHasLoadedSelection(loadedSelectionNow);

  const bulkMutation = useMutation({
    // Droga domyślna serwera (bez `routeKey`), bez komentarza — decyzje wymagające okna decyzji
    // wracają jako pominięte z powodem (patrz `splitBulkOutcome`).
    mutationFn: (targetRows: MyPendingDecisionRow[]) =>
      bulkSubmitDecisionsInBatches(
        targetRows.map(
          (row): BulkDecisionItem => ({
            stepInstanceId: row.stepInstanceId,
            decisionType: 'approve',
          }),
        ),
      ),
    onSuccess: (response, targetRows) => {
      const { accepted, skipped, keepSelectedIds } = splitBulkOutcome(t, targetRows, response);
      // Zaakceptowane znikną z listy po odświeżeniu — także te, w których głos oddano, a etap czeka
      // na pozostałych akceptantów (lista nie pokazuje kroków, w których już zagłosowano).
      // Pominięte ZOSTAJĄ zaznaczone (user może je rozstrzygnąć pojedynczo) — poza anulowanymi
      // w międzyczasie: te znikają z listy, więc ich id nie zostaje w zaznaczeniu.
      void queryClient.invalidateQueries({ queryKey: ['processes'] });
      // Zbiór rekordów sekcji się zmienił — podpowiedzi filtra sekcji (liczone z jej rekordów).
      void invalidateSectionDistinctValues(queryClient);
      setMarkedIds(keepSelectedIds);
      setBulkConfirmOpen(false);
      if (skipped.length === 0) {
        toast.show({
          variant: 'success',
          title: t('tenant.processes.approvals.bulk.acceptedToast', { count: accepted }),
        });
      } else {
        setBulkResult({ accepted, skipped });
      }
    },
    onError: () => {
      setBulkConfirmOpen(false);
      toast.show({
        variant: 'error',
        title: t('tenant.processes.approvals.bulk.resultError'),
      });
    },
  });

  // Zaznaczenie ograniczone do bieżącej strony — po zmianie strony czyścimy.
  function changePage(next: number) {
    setMarkedIds([]);
    setOffset(next);
  }

  // Zmiana filtra resetuje `offset` do 0 (mirror `ActiveInstancesTab`/
  // `MySubmittedInstancesTab`) — inaczej user mógłby wylądować na pustej stronie
  // po zawężeniu wyniku. Zaznaczenie czyścimy też — zbiór wierszy pod nim się zmienia.
  function handleHeaderFiltersChange(next: ColumnHeaderFilters) {
    setHeaderFilters(next);
    setMarkedIds([]);
    setOffset(0);
  }

  // Sort server-side (kontrolowany) — reset offset + zaznaczenia (zbiór się zmienia).
  function handleSort(column: string, direction: 'asc' | 'desc') {
    setSortColumn(column);
    setSortDirection(direction);
    setMarkedIds([]);
    setOffset(0);
  }

  // Komunikat o niewczytanych firmach dla czytnika ekranu: STAŁY region `role="status"` (wizualnie
  // ukryty, poza tabelą), do którego treść trafia dopiero przy błędzie — pusty region obecny od
  // początku jest zapowiadany przy zmianie treści, element dołożony razem z treścią często nie. Wizualna
  // podpowiedź jest w nagłówku kolumny „Firma" (`aria-hidden`, patrz `buildColumns`).
  const companyOptionsStatus = (
    <div role="status" className="sr-only" data-testid="approvals-company-options-status">
      {companyLoadFailed ? t('tenant.processes.approvals.companyOptionsError') : null}
    </div>
  );

  if (isLoading) {
    return (
      <>
        <div className="text-sm text-text-tertiary" data-testid="approvals-loading">
          {t('tenant.processes.approvals.loading')}
        </div>
        {companyOptionsStatus}
      </>
    );
  }

  if (isError) {
    // 403 „brak uprawnienia zespołu" — łagodny fallback zamiast surowego błędu (mirror
    // `ActiveInstancesTab`). W praktyce kafelek „Zespół" i tak jest wtedy ukryty (zasilany
    // tym samym uprawnieniem), więc to głównie zabezpieczenie na deep-link/utratę uprawnienia
    // w trakcie sesji. Działa identycznie w obu trybach (płaskim/grupowanym) — `currentError`
    // wskazuje na aktywny tryb (`isGrouped`).
    const status = currentError instanceof AxiosError ? currentError.response?.status : undefined;
    if (scope === 'team' && status === 403) {
      return (
        <div className="text-sm text-text-tertiary" data-testid="approvals-team-permission-denied">
          {t('tenant.processes.approvals.teamPermissionDenied')}
        </div>
      );
    }
    return (
      <div className="text-sm text-error" data-testid="approvals-load-error">
        {t('tenant.processes.approvals.loadError')}
      </div>
    );
  }

  // Pusty stan (brak wierszy / brak koszyków) renderuje się W ŚRODKU tabeli przez wbudowany
  // empty-state `ConfigurableTable` (nagłówki kolumn zostają) — `emptyTitle`/`emptyAction`
  // niżej przy wywołaniu. Filtr per kolumna → wbudowane „Wyczyść filtry"; `overdueOnly` (którego
  // wewnętrzne `hasActiveFilters` tabeli NIE liczy) → własna furtka `emptyAction`/`clearOverdueFilter`.

  // Selektor grupowania + przełącznik auto-odświeżania — jedne kontrolki w dwóch miejscach: w pasku
  // WEWNĄTRZ tabeli (`toolbarExtra`) albo, w trybie sekcji kartotek, w osobnym pasku nad sekcjami
  // (tabela pozostałych grup bywa wtedy nieobecna, a sekcje nie mają paska z tymi kontrolkami).
  const groupingControls = (
    <>
      <div className="flex items-center gap-1.5">
        <span className="whitespace-nowrap text-sm text-text-tertiary">
          {t('tenant.processes.approvals.groupBy.label')}
        </span>
        <Dropdown
          options={groupByOptions}
          value={groupBy ?? ''}
          onChange={handleGroupByChange}
          aria-label={t('tenant.processes.approvals.groupBy.label')}
          className="w-48"
          data-testid="approvals-groupby-select"
        />
      </div>
      <Button
        variant={autoRefresh ? 'secondary' : 'ghost'}
        size="sm"
        leftIcon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
        aria-pressed={autoRefresh}
        onClick={() => setAutoRefresh((v) => !v)}
        data-testid="approvals-auto-refresh-toggle"
      >
        {t('tenant.processes.approvals.autoRefresh')}
        {autoRefresh ? ' ✓' : ''}
      </Button>
    </>
  );
  // Przełącznik „Lista / Karty" — zamiast wbudowanego przełącznika tabeli (tabele są w trybie
  // sterowanym, patrz opis pliku): w pasku tabeli zwykłej listy albo w pasku trybu sekcji.
  const viewModeToggle = (
    <DecisionViewModeToggle value={viewMode} onChange={setViewMode} testIdPrefix="approvals" />
  );
  // Tabela pozostałych grup w trybie sekcji — tylko gdy zostały jakieś kartoteki bez sekcji; gdy nie
  // ma żadnych grup (pusty wynik), tabela zostaje, bo to ona niesie empty-state i furtkę „Wyczyść".
  const showTable = !sectionsMode || tableGroups.length > 0 || sections.length === 0;
  // Jedyny pasek akcji masowej w trybie sekcji (zaznaczenie jest rozrzucone po kilku tabelach, więc
  // żadna nie ma własnego): tylko zakres „Moje" i tylko na danych własnych — jak pasek tabeli.
  const showSectionsBulkBar =
    sectionsMode && !readOnly && !showingPlaceholder && selectedIds.length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {companyOptionsStatus}
      {/* Zakres „Zespół" (`?scope=team`) — widok TYLKO-PODGLĄD (przyciski decyzji, multi-select
          i pasek masowych akcji wyłączone niżej, `readOnly`; zostaje „Otwórz formularz"). Notka
          nad tabelą, żeby user nie próbował decydować za nieobecnego przełożonego. */}
      {readOnly && (
        <div
          className="flex shrink-0 items-center gap-2"
          data-testid="approvals-team-readonly-note"
        >
          <Badge tone="info" size="sm">
            {t('tenant.processes.approvals.teamReadOnlyNote')}
          </Badge>
        </div>
      )}

      {/* Pasek wartości/podsumy — samodzielna linia nad tabelą (wzorzec SellCall: osobna
          linia "wyniki" nad właściwym paskiem narzędzi). Suma „Wartość na liście" (per
          waluta, z serwera — po WSZYSTKICH stronach, dedup dokumentu) ukryta w trybie
          grupowanym (`flatQuery` jest wtedy wyłączone, więc `sumsByCurrency` byłoby
          nieaktualne/puste; każdy koszyk niesie WŁASNĄ podsumę w nagłówku grupy). `shrink-0`
          — stała wysokość, nie bierze udziału w wypełnianiu reszty ekranu przez tabelę. */}
      {isGrouped ? (
        <div
          className="flex shrink-0 flex-wrap items-center gap-2 text-sm"
          data-testid="approvals-groups-info"
        >
          <span className="text-text-tertiary">
            {t('tenant.processes.approvals.groupBy.summaryCount', { count: groupedTotalRows })}
          </span>
        </div>
      ) : (
        <div
          className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm"
          data-testid="approvals-list-value"
        >
          <span className="text-text-tertiary">{t('tenant.processes.approvals.listValue')}:</span>
          {sumsByCurrency.length === 0 ? (
            <span className="text-text-tertiary">—</span>
          ) : (
            sumsByCurrency.map((s) => (
              <span
                key={s.currencyCode}
                className="font-medium tabular-nums"
                data-testid={`approvals-list-value-${s.currencyCode}`}
              >
                {formatMoney(s.total, s.currencyCode)}
              </span>
            ))
          )}
        </div>
      )}

      {/* Tabela wypełnia resztę wysokości ekranu (mirror Contractors.tsx) — `min-h-0
          flex-1 overflow-hidden` na tym wrapperze + `fillHeight` na ConfigurableTable,
          która sama robi wtedy jeden `overflow-auto` (obie osie) zamiast rosnąć ze
          stroną. Kreator Listy (ConfigurableTable): multi-select (masowe akceptacje) +
          inline akcje per wiersz (renderActions) + filtry/sort per kolumna server-side.
          `allowedViewModes={['list','cards']}` — Kanban dla tych danych bezużyteczny.
          `titleColumnId="target_label"` — sensowny tytuł karty (nazwa dokumentu), bez
          przestawiania kolejności kolumn listy. `persistConfig={false}` — brak zapisu
          layoutu (mirror InstanceListingTable.tsx).
          `toolbarExtra` — selektor grupowania + przełącznik auto-odświeżania + przełącznik
          „Lista / Karty" (`viewModeToggle`, bo tabela jest w trybie sterowanym), wstrzyknięte
          w WEWNĘTRZNY pasek tabeli (wzorzec SellCall: jeden wspólny pasek z przełącznikiem
          Tabela/Karty, zamiast osobnego paska nad tabelą).
          `stickyActionsColumn` — kolumna akcji („Decyzja") przypięta do prawej krawędzi widocznej
          części tabeli (przy szerokiej tabeli nie trzeba przewijać w bok do przycisków),
          o ile zajmuje najwyżej połowę widocznej szerokości (reguła w `ConfigurableTable`).
          Zakres „Zespół" (`readOnly`) — `selectable`/`renderBulkActions` wyłączone (undefined),
          więc `ConfigurableTable` nie renderuje ani checkboxów, ani paska masowych akcji, a
          kolumna akcji („Akcje") niesie tylko „Otwórz formularz" — user może przeglądać i
          wchodzić na kartę rekordu (klik wiersza albo ten przycisk), nie decydować za podwładnego.
          Tryb sekcji kartotek (`sectionsMode`): nad treścią osobny pasek z `groupingControls`,
          przełącznikiem „Lista / Karty" i (przy zaznaczeniu) jedynym paskiem akcji masowej, a treść
          to przewijana kolumna — sekcje kartotek (`DecisionSections`) + (gdy zostały kartoteki bez
          sekcji) ta sama tabela, ale BEZ `fillHeight` (rośnie z treścią zamiast wypełniać resztę
          ekranu), bez własnego paska `toolbarExtra` i bez własnego paska zaznaczenia. */}
      {sectionsMode && (
        <div
          className="flex shrink-0 flex-wrap items-center gap-2"
          data-testid="approvals-sections-toolbar"
        >
          {/* Stały region dla czytnika ekranu (sam pasek pojawia się dopiero przy zaznaczeniu).
              Licznik pokazuje to samo co pasek (`showSectionsBulkBar`), więc na danych-zastępniku
              spada do 0 — ŚWIADOMIE nie liczymy `selectedIds` wprost: na danych-zastępniku wchodzą do
              niego wiersze poprzedniej odpowiedzi tabeli pozostałych kartotek, które w prawdziwej
              odpowiedzi mogą już nie istnieć, więc zapowiedź „Zaznaczono: N" mogłaby być nieprawdziwa
              (a pasek i `selectable` są wtedy wyłączone z tego samego powodu). W trybie sekcji dane-
              zastępnik w praktyce nie występuje: klucz zapytania o koszyki zmienia się tylko razem z
              widokiem (zakres, „Przeterminowane", wymiar), a po zmianie widoku danych-zastępnika nie
              pokazujemy (pokolenie widoku) — więc zmiana tego wyrażenia nic by nie dała ani nie dałaby
              się sprawdzić testem na prawdziwej ścieżce. */}
          <DecisionSectionsSelectionStatus count={showSectionsBulkBar ? selectedIds.length : 0} />
          {showSectionsBulkBar && (
            <DecisionSectionsBulkBar
              count={selectedIds.length}
              onApprove={() => setBulkConfirmOpen(true)}
              onClear={() => setMarkedIds([])}
            />
          )}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {groupingControls}
            {sections.length > 0 && viewModeToggle}
          </div>
        </div>
      )}
      {/* Dane-zastępnik (`showingPlaceholder`: poprzednie wiersze do czasu odpowiedzi po zmianie
          filtra/sortu/strony): kontener `aria-busy` i lekko przyciemniony; zaznaczanie i akcja masowa
          wyłączone (`selectable`, kolumna z checkboxami znika do czasu odpowiedzi), a przyciski decyzji
          w wierszu ZOSTAJĄ — okno decyzji dociąga świeży stan sprawy, a serwer i tak odrzuci decyzję na
          nieaktualnym kroku („nie czeka już na Twoją decyzję"). */}
      <div
        aria-busy={showingPlaceholder || undefined}
        data-testid="approvals-table-region"
        className={cn(
          sectionsMode
            ? 'flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto'
            : 'flex min-h-0 flex-1 flex-col overflow-hidden',
          showingPlaceholder && 'opacity-60 transition-opacity',
        )}
      >
        {sectionsMode && (
          // `key={scope}`: po zmianie zakresu („Moje" ↔ „Zespół") sekcje startują od nowa (zwinięte,
          // bez filtrów i strony z poprzedniego zakresu), tak jak rozwinięcie grup tabeli.
          <DecisionSections
            key={scope}
            sections={sections}
            overdue={overdueOnly}
            scope={scope}
            refetchInterval={autoRefresh && !pauseAuto ? AUTO_REFRESH_MS : false}
            onOpenDecision={(row, target) => setDecision({ row, target })}
            viewMode={viewMode}
            selection={sectionSelection}
          />
        )}
        {showTable && (
          <ConfigurableTable<MyPendingDecisionRow>
            fillHeight={!sectionsMode}
            className={sectionsMode ? 'shrink-0' : undefined}
            tableId="processes-my-pending-decisions"
            testIdPrefix="approvals"
            columns={tableColumns}
            // Tryb sekcji: tabela pozostałych kartotek bez wiersza filtrów (kolumny nie mają też
            // `headerFilter` ani sortowania) — patrz `sectionsMode`/`tableColumns`.
            filterable={!sectionsMode}
            // Tryb grupowany: `data` ignorowane przez ConfigurableTable (renderuje `groups`
            // zamiast płaskiej listy) — przekazujemy `[]` dla czytelności kontraktu.
            data={isGrouped ? [] : rows}
            groups={mappedGroups}
            expandedGroupKeys={isGrouped ? expandedGroupKeys : undefined}
            onToggleGroup={isGrouped ? toggleGroupExpand : undefined}
            getRowId={(row) => row.stepInstanceId}
            selectable={!readOnly && !showingPlaceholder}
            selectedIds={tableSelectedIds}
            // Tryb sekcji: zmiana zaznaczenia tej tabeli podmienia tylko jej wycinek (reszta należy do
            // sekcji); poza nim zaznaczenie jest całe tej tabeli, jak dotąd.
            onSelectionChange={
              sectionsMode ? (next) => handleSelectionSliceChange(tableStepIds, next) : setMarkedIds
            }
            // Tryb widoku sterowany z zakładki (wspólny z sekcjami) — wbudowany przełącznik tabeli jest
            // wtedy ukryty, więc zastępuje go `viewModeToggle` w `toolbarExtra` (tylko zwykła lista:
            // tabela grupowana zawsze jest listą, a w trybie sekcji przełącznik stoi w pasku sekcji).
            viewMode={viewMode}
            allowedViewModes={['list', 'cards']}
            titleColumnId="target_label"
            toolbarExtra={
              sectionsMode ? undefined : (
                <>
                  {groupingControls}
                  {!isGrouped && viewModeToggle}
                </>
              )
            }
            sortColumn={sortColumn ?? undefined}
            sortDirection={sortDirection}
            onSort={handleSort}
            // „Zespół" (tylko podgląd) ma w tej kolumnie wyłącznie „Otwórz formularz", bez decyzji —
            // nagłówek „Decyzja" byłby tam mylący, stąd ogólne „Akcje".
            actionsColumnLabel={
              readOnly ? t('common.actions') : t('tenant.processes.approvals.inline.columnLabel')
            }
            // Kolumna akcji ma szerokość swojej treści (przyciski w jednej linii, wariant
            // `layout="list"` poniżej), a nie sztywne 380 px — to było dobrane pod przyciski
            // zawijane jeden pod drugim i przy jednej linii zostawiałoby puste pole albo
            // wymuszało ucinanie. Zostaje domyślny próg tabeli (120 px; najwęższy wiersz to
            // „Akceptacja" + „Otwórz formularz", w „Zespole" i w wierszu nienawigowalnym jeszcze
            // węższy: sam jeden przycisk); szersze wiersze rozpychają kolumnę do swojej treści
            // (komórka akcji ma `whitespace-nowrap`, a każdy przycisk drogi jest ograniczony do 10rem).
            // Kolumna rośnie do treści, bo tabela ma szerokość `auto` (`min-w-full`, bez jawnej
            // szerokości), więc przeglądarka liczy układ automatyczny mimo `tableLayout: 'fixed'`;
            // jawna szerokość tabeli zepchnęłaby kolumnę do `actionsColumnWidth` (120 px).
            stickyActionsColumn
            renderActions={(row, { placement }) => (
              // stopPropagation zapewnia framework: ConfigurableTable owija renderActions
              // w <td onClick=stopPropagation>, CardsView analogicznie — klik akcji nie
              // nawiguje bez dodatkowej warstwy tutaj. Przyciski dróg otwierają okno
              // „Akceptacja" tej sprawy (`PendingDecisionDialog`, renderowane na dole —
              // poza tabelą, więc klik w oknie nie dociera do `onRowClick`). „Otwórz formularz"
              // jest w KAŻDYM wierszu (tabela i karty) i robi dokładnie to, co klik wiersza;
              // wiersz nienawigowalny (klik nic nie robi) go nie ma. W „Zespole" (`readOnly`)
              // to jedyna akcja wiersza — bez przycisków decyzji. Na wąskiej karcie kontener
              // zawija się (`flex-wrap`), w tabeli zostaje jedna linia (kolumna rośnie do treści).
              <div
                className={cn(
                  'flex items-center justify-end gap-3',
                  placement === 'cards' && 'flex-wrap',
                )}
                data-testid={`approvals-row-actions-${row.stepInstanceId}`}
              >
                {!readOnly && (
                  <DecisionButtons
                    routes={row.availableRoutes}
                    size="sm"
                    // Układ listy (jedna linia, nazwa drogi do 10rem z dymkiem przy ucięciu)
                    // tylko w TABELI, gdzie kolumna jest przypięta; karta jest wąska, więc ma
                    // zwykły układ (zawijane przyciski, nazwa do 14rem) jak dotąd.
                    layout={placement === 'table' ? 'list' : undefined}
                    groupLabel={t('tenant.processes.approvals.inline.groupLabel', {
                      number: row.instanceNumber,
                    })}
                    stepBackAvailable={row.stepBackAllowed}
                    onOpenStepBack={() => setDecision({ row, target: { kind: 'stepBack' } })}
                    onOpen={(routeKey) => setDecision({ row, target: { kind: 'route', routeKey } })}
                    testIdPrefix={`approvals-decision-${row.stepInstanceId}`}
                  />
                )}
                {isTargetNavigable(row.targetModuleCode, row.targetRecordId) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="shrink-0 whitespace-nowrap"
                    onClick={() => navigateToTarget(row.targetModuleCode, row.targetRecordId)}
                    // Numer sprawy w nazwie dostępnej odróżnia identyczne przyciski w kolejnych wierszach.
                    aria-label={t('tenant.processes.approvals.openFormAria', {
                      number: row.instanceNumber,
                    })}
                    data-testid={`approvals-open-form-${row.stepInstanceId}`}
                  >
                    {t('tenant.processes.approvals.openForm')}
                  </Button>
                )}
              </div>
            )}
            // Pasek zaznaczenia tabeli — tylko poza trybem sekcji (tam jest JEDEN wspólny pasek w
            // pasku trybu sekcji, `DecisionSectionsBulkBar`, bo zaznaczenie obejmuje kilka tabel).
            renderBulkActions={
              readOnly || sectionsMode
                ? undefined
                : () => (
                    <DecisionBulkActions
                      onApprove={() => setBulkConfirmOpen(true)}
                      onClear={() => setMarkedIds([])}
                    />
                  )
            }
            headerFilters={headerFilters}
            onHeaderFiltersChange={handleHeaderFiltersChange}
            persistConfig={false}
            onRowClick={(row) => navigateToTarget(row.targetModuleCode, row.targetRecordId)}
            // Mirror `InstanceListingTable.tsx` — `ConfigurableTable` dokleja
            // `cursor-pointer hover:bg-gray-50` do KAŻDEGO wiersza gdy `onRowClick` jest
            // podany, niezależnie od per-wiersza klikalności. `!cursor-default`
            // (important modifier) wymusza nadpisanie dla wierszy bez `targetUrl`
            // (nieznany/`custom.*` moduł); `hover:!bg-transparent` gasi podświetlenie,
            // żeby wiersz nieklikalny nie udawał interaktywnego.
            rowClassName={(row) =>
              isTargetNavigable(row.targetModuleCode, row.targetRecordId)
                ? ''
                : '!cursor-default hover:!bg-transparent'
            }
            emptyTitle={t(
              scope === 'team'
                ? 'tenant.processes.approvals.empty.team'
                : 'tenant.processes.approvals.empty.pending',
            )}
            // Furtka wyjścia, gdy `?overdue=true` daje zero wyników — `ConfigurableTable`
            // NIE wie nic o `overdueOnly` (jego własne `hasActiveFilters` liczy tylko
            // columnFilters/headerFilters), więc bez tego pokazałby ogólny `emptyTitle`
            // bez żadnej akcji. Patrz `clearOverdueFilter` wyżej.
            emptyAction={
              // `overdueOnly` jest już `false` w zakresie „Zespół" (patrz definicja wyżej:
              // `scope !== 'team' && searchParams.get('overdue') === 'true'`) — bez
              // dodatkowego warunku tutaj.
              overdueOnly ? (
                <Button variant="ghost" size="sm" onClick={clearOverdueFilter}>
                  {t('tenant.processes.approvals.clearOverdueFilter')}
                </Button>
              ) : undefined
            }
          />
        )}
      </div>
      {/* Paginacja (trybu płaskiego) — ukryta w trybie grupowanym (nagłówki koszyków są
          agregacją SQL bez paginacji; wiersze KAŻDEGO koszyka mają WŁASNĄ mini-paginację
          w jego `footer`, patrz `mappedGroups`). `shrink-0` — stała wysokość pod tabelą
          wypełniającą resztę ekranu. */}
      {!isGrouped && (
        <div className="shrink-0">
          <PaginationControls
            total={total}
            offset={offset}
            limit={PAGE_LIMIT}
            onOffsetChange={changePage}
          />
        </div>
      )}

      {/* Okno „Akceptacja" dla jednej sprawy (przyciski dróg w wierszu). */}
      {decision && (
        <PendingDecisionDialog
          row={decision.row}
          initialRouteKey={decision.target.kind === 'route' ? decision.target.routeKey : null}
          initialStepBack={decision.target.kind === 'stepBack'}
          onClose={() => {
            setDecision(null);
            // Zamknięcie okna bez decyzji bywa następstwem wykrycia nieaktualnej sprawy („nie czeka
            // już na Twoją decyzję" — okno samo unieważnia dane procesów): zbiór rekordów sekcji
            // mógł się zmienić, więc odświeżamy też podpowiedzi filtra sekcji.
            void invalidateSectionDistinctValues(queryClient);
          }}
          onDecided={() => {
            // Rozstrzygnięta sprawa schodzi z zaznaczenia (jej wiersz zniknie po odświeżeniu listy).
            const decidedStepId = decision.row.stepInstanceId;
            setMarkedIds((prev) => prev.filter((id) => id !== decidedStepId));
            setDecision(null);
            // Zbiór rekordów sekcji się zmienił — podpowiedzi filtra sekcji (liczone z jej rekordów).
            void invalidateSectionDistinctValues(queryClient);
          }}
          testIdPrefix="approvals-decision-dialog"
        />
      )}

      {/* „Akceptuj zaznaczone" — pytanie z liczbą spraw; bez wyboru drogi i komentarza (serwer
          bierze drogę domyślną). Zamknięcie w trakcie wysyłki zablokowane. */}
      <ConfirmDialog
        open={bulkConfirmOpen}
        variant="primary"
        title={t('tenant.processes.approvals.bulk.confirmTitle', { count: selectedRows.length })}
        message={t('tenant.processes.approvals.bulk.confirmMessage')}
        confirmLabel={t('tenant.processes.approvals.bulk.confirmLabel')}
        confirming={bulkMutation.isPending}
        onConfirm={() => bulkMutation.mutate(selectedRows)}
        onClose={() => {
          if (!bulkMutation.isPending) setBulkConfirmOpen(false);
        }}
      />

      {/* Wynik akcji masowej z pominiętymi sprawami (pełny sukces kończy się samym dymkiem). */}
      {bulkResult && (
        <BulkAcceptResultDialog
          open
          accepted={bulkResult.accepted}
          skipped={bulkResult.skipped}
          onClose={() => setBulkResult(null)}
        />
      )}
    </div>
  );
}
